import { createLogger } from '@utils/Logger'
import { Brand } from '@typescript'

const log = createLogger('AuthJwtBrand')

export type JwtPayload = Record<string, any>

export function decodeJwtPayload(token: string | undefined): { payload?: JwtPayload; error?: Error } {
  try {
    if (!token || typeof token !== 'string') throw new Error('Invalid token')
    const parts = token.split('.')
    if (parts.length !== 3) throw new Error('Unexpected token format')

    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    )

    const payload = JSON.parse(json) as JwtPayload
    return { payload }
  } catch (error: any) {
    if (process.env.NODE_ENV === 'development') {
      log.warn('Failed to decode JWT payload', { error })
    }
    return { error: error instanceof Error ? error : new Error(String(error)) }
  }
}

export function getJwtBrandId(token: string): string | '' {
  const { payload } = decodeJwtPayload(token)
  if (!payload) return ''
  return (payload.brand_id || payload.brandId || payload['brand-id'] || '') as string
}

/** Top-level `party_id` on the Mako authorization JWT (`X-JWT-Token`); same claim as mkback `JwtTokenUtils#getPartyId`. */
export function getPartyIdFromMakoJwt(token: string | undefined): string | undefined {
  const { payload } = decodeJwtPayload(token)
  return payload?.party_id
}

export function getExpectedBrandIdForBrand(brand: string | undefined | null): string | '' {
  if (!brand) return ''
  if (/^\d{3}$/.test(brand)) return brand
  try {
    const def = Brand.fromShortName(brand)
    return Brand.getNumber(def)
  } catch {
    return ''
  }
}

/**
 * Normalizes brand ID to handle EVEREST normalization (003 -> 001)
 * This matches the backend Brand.getNumber() behavior
 */
function normalizeBrandId(brandId: string | ''): string | '' {
  if (!brandId) return ''
  // EVEREST (003) normalizes to 001, matching backend behavior
  if (brandId === '003') return '001'
  return brandId
}

// Validate JWT brand id against the selected brand, logging along the way.
export function validateJwtBrand(
  logger: { debug: (...args: any[]) => void; error: (...args: any[]) => void; warn: (...args: any[]) => void },
  token: string,
  selectedBrand: string | undefined | null,
): { ok: true } | { ok: false; error: Error } {
  try {
    // Check if token decoding failed
    const decodeResult = decodeJwtPayload(token)
    if (decodeResult.error) {
      logger.warn('Failed to process JWT brand-id validation', decodeResult.error)
      return { ok: true }
    }

    const jwtBrandId = getJwtBrandId(token)
    const expectedBrandId = getExpectedBrandIdForBrand(selectedBrand)

    if (expectedBrandId && jwtBrandId) {
      // Handle EVEREST normalization: if JWT has '003' and expected brand is EVEREST (ebac),
      // normalize both for comparison. Otherwise, compare raw values to preserve brand distinction.
      const isEverestJwt = jwtBrandId === '003'
      const isEverestExpected = selectedBrand && (selectedBrand.toLowerCase() === 'ebac' || selectedBrand.toLowerCase() === 'ev' || selectedBrand === '003')
      
      let compareJwtBrandId = jwtBrandId
      let compareExpectedBrandId = expectedBrandId
      
      // Only normalize if both are EVEREST-related
      if (isEverestJwt && isEverestExpected) {
        compareJwtBrandId = normalizeBrandId(jwtBrandId)
        compareExpectedBrandId = normalizeBrandId(expectedBrandId)
      }
      
      if (compareJwtBrandId !== compareExpectedBrandId) {
        // Create error with BRAND_MISMATCH constant for UI parsing
        const err = new Error('BRAND_MISMATCH')
        logger.error('Brand mismatch detected', { jwtBrandId, expectedBrandId, selectedBrand })
        return { ok: false, error: err }
      }
    }

    return { ok: true }
  } catch (e) {
    logger.warn('Failed to process JWT brand-id validation', e)
    return { ok: true }
  }
}
